import type { PayloadAction } from '@reduxjs/toolkit';
import { streamingDataSlice, type BaseWsQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { AppState, AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import { getErrorMessage } from '@src/providers/AppStateProvider/wsEndpointBuilder';
import {
  ExecTypeEnum,
  globalToastManagerUI,
  logger,
  NotificationVariants,
  type ExecutionReport,
  type Order,
} from '@talos/kyoko';
import { addOtherOrderCardsData } from '../../Cards/ActiveOrderCardsSlice';

const MODIFY_ORDER_INFO_TIMEOUT_MS = 10_000;

interface ModifyOrderCardDataParams {
  orderID: string;
  orderStartTime?: string;
}

type ModifyOrderCardDataQuery = BaseWsQuery<{
  OrderID: string;
  StartDate?: string;
  SendMarkets: boolean;
  HideAPICalls: boolean;
  Throttle: string;
  CoalesceExecs: ExecTypeEnum[];
}>;

export function shouldLoadModifyOrderCardData(
  state: AppState,
  order: Order | ExecutionReport
): order is (Order | ExecutionReport) & { OrderID: string } {
  // We only check openOrdersData and not otherCardsData because OtherCardsData is just a stale list of orders. Even if
  // something exists in there, it might not be live. Open Orders will always be backed up by a live subscription.
  // (Yes, more to do here!)
  return !!order.OrderID && !state.cards.openOrdersData.has(order.OrderID);
}

function buildModifyOrderCardDataQuery({
  orderID,
  orderStartTime,
}: ModifyOrderCardDataParams): ModifyOrderCardDataQuery {
  const query: ModifyOrderCardDataQuery = {
    tag: 'ORDER_SLICE_MODIFY_CARD',
    OrderID: orderID,
    SendMarkets: true,
    HideAPICalls: true,
    Throttle: '1s',
    CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
  };

  // StartDate lets the backend hit its order index directly for a faster lookup when we know SubmitTime.
  if (orderStartTime) {
    query.StartDate = orderStartTime;
  }

  return query;
}

type ModifyOrderAction = PayloadAction<{ order: Order | ExecutionReport }>;

export function startModifyOrderCardDataListener(startListening: AppStateListenerStart) {
  return startListening({
    predicate: (action): action is ModifyOrderAction => action.type === 'orders/modifyOrder',
    effect: async (action, listener) => {
      listener.cancelActiveListeners();

      if (!shouldLoadModifyOrderCardData(listener.getState(), action.payload.order)) {
        return;
      }

      const orderID = action.payload.order.OrderID;
      const queryArgs = buildModifyOrderCardDataQuery({
        orderID,
        orderStartTime: action.payload.order.SubmitTime,
      });
      const selectCacheEntry = streamingDataSlice.endpoints.getOrders.select(queryArgs);
      const selectOrder = (state: AppState) => selectCacheEntry(state).data?.get(orderID);

      // The query getting back a result can be represented by either success or error
      const hasInitialQueryResult = (state: AppState) => {
        const cacheEntry = selectCacheEntry(state);
        return cacheEntry.isSuccess || cacheEntry.isError;
      };

      const shouldStopListening = (state: AppState) => state.order.orderBeingModified?.OrderID !== orderID;

      const waitForInitialQueryResult = async () => {
        // The cache entry might already exist!
        if (hasInitialQueryResult(listener.getState())) {
          return;
        }

        await listener.condition(() => {
          const state = listener.getState();
          return shouldStopListening(state) || hasInitialQueryResult(state);
        }, MODIFY_ORDER_INFO_TIMEOUT_MS);
      };

      const dispatchLatestOrderCardData = () => {
        const order = selectOrder(listener.getState());
        if (order == null) {
          return;
        }

        listener.dispatch(
          addOtherOrderCardsData({
            order,
            submitTime: order.SubmitTime,
          })
        );
      };

      const unsubscribeCacheListener = startListening({
        predicate: (_, next, prev) => selectOrder(next) !== selectOrder(prev),
        effect: dispatchLatestOrderCardData,
      });
      const querySubscription = listener.dispatch(streamingDataSlice.endpoints.getOrders.initiate(queryArgs));

      try {
        // The "working" part of this listener is split into two parts: initial lookup, and then continuous listening thereafter.
        // We do this because we need a way to react to the case where the initial lookup errors or timeouts, so we can raise an error to the user.
        await waitForInitialQueryResult();
        dispatchLatestOrderCardData();

        const cacheError = selectCacheEntry(listener.getState()).error;
        if (selectOrder(listener.getState()) == null || cacheError) {
          // Either the backend returned with an empty data array, or the backend returned a ws error message. Either way, we failed.
          logger.error(new Error(`Unable to find order for modification`), {
            extra: {
              orderID,
              error: cacheError ? new Error(`WebSocket error: ${getErrorMessage(cacheError)}`) : undefined,
            },
          });
          globalToastManagerUI.addToast({
            text: `Unable to find order ${orderID} for modification. This is expected for API orders`,
            variant: NotificationVariants.Warning,
            timeout: 5000,
          });
        }

        // Initial lookup is complete. We keep listening until something changes to make this listener instance irrelevant.
        if (!shouldStopListening(listener.getState())) {
          await listener.condition(() => shouldStopListening(listener.getState()));
        }
      } finally {
        // Try/finally to have a tidy way to always call these cleanups
        querySubscription.unsubscribe();
        unsubscribeCacheListener();
      }
    },
  });
}
